import React, { useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import handleException from "go-core/api/handleException";
import { ApiError } from "go-core/api/types";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { useCustomErrors } from "go-form/hooks";
import { CustomValidationConfig, CustomValidationConstraint } from "go-form/services/types";
import { adminApi } from "../../../../../../../../../services/Api/Admin/adminApi";
import { SearchSupportDistrictApi, SupportDistrictApi } from "../../../../../../../../../services/support/types";

interface Props {
	setData: React.Dispatch<React.SetStateAction<SupportDistrictApi[]>>;
	setDataLoading: React.Dispatch<React.SetStateAction<boolean>>;
	data: SupportDistrictApi[];
	dataLoading: boolean;
}

const SearchDistrictsForm = ({ setData, setDataLoading, data, dataLoading }: Props) => {
	const { t } = useTranslation("admin");
	const form = useForm<SearchSupportDistrictApi>({
		criteriaMode: "all",
		defaultValues: {
			city: "",
			districts: [],
			street: "",
		},
	});
	const {
		handleSubmit,
		formState: { errors },
		setError,
		register,
		watch,
		control,
		clearErrors,
		getValues,
		setValue,
	} = form;
	const isMobile = useWindowSize().isMobile;
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);
	const { fields, remove, append } = useFieldArray({ name: "districtList", control });
	const { addFlash } = useFlash();
	const [formErrors, setFormErrors] = useState<ApiError[]>([]);
	const [districts, setDistricts] = useState<string[]>([]);
	const [loadingDistricts, setLoadingDistricts] = useState<boolean>(false);
	const [searchDataFetched, setSearchDataFetched] = useState(false);

	const textInputValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
			utils: { watch },
		}),
		[setErrors, watch]
	) satisfies CustomValidationConfig;

	const getParsedDataForm = (data: SearchSupportDistrictApi) => {
		data.districts = (data.districtList || []).map((district) => district.name).filter((name) => name !== "");
		delete data.districtList;

		return data;
	};

	const handleSearch = handleSubmit(async (data) => {
		clearErrors();
		if (!validateCustomErrors()) return;

		setValue("districts", []);
		setValue("districtList", []);
		fetchDistricts();
		setSearchDataFetched(false);
		setDataLoading(true);
		try {
			const res = await adminApi.getCurrentDistricts(data);
			setData(res);
		} catch (err) {
			const errs = handleException(err);
			setFormErrors(errs);
			handleError.form(err, setError, addFlash);
		}
		setDataLoading(false);
		setSearchDataFetched(true);
	});

	const fetchDistricts = async () => {
		setLoadingDistricts(true);

		try {
			const res = await adminApi.getDistricts(getValues("city"));
			setDistricts(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoadingDistricts(false);
	};

	const onSubmit = handleSubmit(async (data) => {
		setFormErrors([]);

		if (!validateCustomErrors()) return;

		setDataLoading(true);
		try {
			const parsedData = getParsedDataForm(data);
			const res = await adminApi.searchSupportDistricts(parsedData);
			setData(res);
		} catch (err) {
			const errs = handleException(err);
			setFormErrors(errs);
			handleError.form(err, setError, addFlash);
		}
		setDataLoading(false);
	});

	const handleAppend = () => {
		append({ name: "" });
	};

	const districtsError = formErrors.find(
		(error) => error.code === "size_must_be_between" && error.field === "districts"
	);

	const getDistrictOptions = () => {
		const selectedDistricts = (watch("districtList") || []).map((district) => district.name);
		const uniqueDistricts = districts.filter((district) => !selectedDistricts.includes(district));
		return uniqueDistricts.map((district) => ({
			value: district,
			label: district,
		}));
	};

	return (
		<FormDirty isDirty={false} className={`${isMobile ? "mt-2" : ""}`}>
			<div className="mb-3">
				<FormInput
					name="city"
					errors={errors}
					label={t("lib:common.address.city")}
					register={register}
					customValidationConfig={textInputValidationConfig}
				/>
				<FormInput
					name="street"
					errors={errors}
					label={t("lib:common.address.street")}
					register={register}
					customValidationConfig={textInputValidationConfig}
				/>
				{districtsError && (
					<div className="invalid-feedback ms-0 mt-3" style={{ display: "inline-block" }}>
						{t("lib:constraints.size_must_be_between", {
							min: districtsError.arguments?.min,
							max: districtsError.arguments?.max,
						})}
					</div>
				)}
				<table className="table table-form">
					<thead>
						<tr>
							<th className="action" />
							<th>{t("modules.support.field.district.title")}</th>
							<th className="action" />
						</tr>
					</thead>
					<tbody>
						{fields.map((field, index) => (
							<tr key={field.id}>
								<td className="action" />
								<td>
									<FormSelectGroup
										errors={errors}
										control={control}
										name={`districtList.${index}.name`}
										options={getDistrictOptions()}
									/>
								</td>
								<td className="action">
									<RemoveSVG className="icon" onClick={() => remove(index)} />
								</td>
							</tr>
						))}
					</tbody>
				</table>
				<ButtonLoading
					loading={loadingDistricts}
					variant="add"
					onClick={handleAppend}
					disabled={data.length === 0 || loadingDistricts || districts.length === 0}
				>
					{`+ ${t("lib:common.action.add")}`}
				</ButtonLoading>
			</div>
			<div className="d-flex gap-2">
				<ButtonLoading
					loading={dataLoading}
					onClick={() => {
						clearErrors();
						handleSearch();
					}}
				>
					{t("lib:common.word.search")}
				</ButtonLoading>
				<ButtonLoading
					loading={dataLoading}
					onClick={() => {
						clearErrors();
						onSubmit();
					}}
					disabled={!searchDataFetched}
				>
					{t("lib:common.action.save")}
				</ButtonLoading>
			</div>
		</FormDirty>
	);
};

export default SearchDistrictsForm;
